import { Component, ChangeDetectionStrategy, input, output, signal, inject } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { AuthLoginState } from '../../core/auth/auth.models';
import { UserDTO } from '../../domain/user/user.model';
import { TranslatePipe } from '@ngx-translate/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faChevronRight, faChevronDown, faChevronUp, faUser } from '@fortawesome/free-solid-svg-icons';
import { SidenavPanelComponent, PanelItem } from '../sidenav-panel/sidenav-panel.component';
import { SidenavMenuItem, sidenavMenuItems } from '../navigation/navigation.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'dottnet-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  imports: [
    TranslatePipe,
    FaIconComponent,
    SidenavPanelComponent,
  ],
})
export class SidenavComponent {
  // References for enums
  readonly AuthLoginState = AuthLoginState;

  // FontAwesome icons
  readonly faChevronRight = faChevronRight;
  readonly faChevronDown = faChevronDown;
  readonly faChevronUp = faChevronUp;
  readonly faUser = faUser;

  // Input variables
  readonly sidenav = input.required<MatSidenav>();
  readonly authLoginState = input<AuthLoginState>(undefined);
  readonly user = input<UserDTO>(undefined);

  // State for showing panels
  showSubjectsPanel = signal<boolean>(false);
  showSpecializationsPanel = signal<boolean>(false);

  readonly subjects = input<PanelItem[]>([]);
  readonly specializations = input<PanelItem[]>([]);

  // Emitted when the mobile profile header is tapped; the app shell opens the user menu
  readonly userMenuClick = output<void>();

  // Menu items with expansion state (deep clone to avoid mutating the original)
  menuItems = signal<SidenavMenuItem[]>(structuredClone(sidenavMenuItems));

  private router = inject(Router);

  /**
   * Updates a menu item at the specified path with deep immutability.
   * @param path Array of indices representing the path to the item (e.g., [2, 1] for menuItems[2].children[1])
   * @param updater Function that receives the current item and returns the updated item
   */
  private updateMenuItemAtPath(
    path: number[],
    updater: (item: SidenavMenuItem) => SidenavMenuItem
  ): void {
    if (path.length === 0) return;

    const updateAtDepth = (
      items: SidenavMenuItem[],
      remainingPath: number[],
      depth: number
    ): SidenavMenuItem[] => {
      const [currentIndex, ...rest] = remainingPath;
      return items.map((item, index) => {
        if (index !== currentIndex) return item;

        if (rest.length === 0) {
          return updater(item);
        }

        if (!item.children) return item;

        return {
          ...item,
          children: updateAtDepth(item.children, rest, depth + 1)
        };
      });
    };

    this.menuItems.set(updateAtDepth(this.menuItems(), path, 0));
  }

  private toggleExpandedAtPath(path: number[]): void {
    this.updateMenuItemAtPath(path, item =>
      item.expandable ? { ...item, expanded: !item.expanded } : item
    );
  }

  toggleMenuItem(index: number): void {
    this.toggleExpandedAtPath([index]);
  }

  toggleChildItem(parentIndex: number, childIndex: number): void {
    this.toggleExpandedAtPath([parentIndex, childIndex]);
  }

  onMenuItemClick(item: SidenavMenuItem, index: number): void {
    if (item.action === 'showSubjects') {
      // Toggle subjects panel, close specializations if open
      const isCurrentlyOpen = this.showSubjectsPanel();
      this.showSpecializationsPanel.set(false);
      this.showSubjectsPanel.set(!isCurrentlyOpen);
    } else if (item.action === 'showSpecializations') {
      // Toggle specializations panel, close subjects if open
      const isCurrentlyOpen = this.showSpecializationsPanel();
      this.showSubjectsPanel.set(false);
      this.showSpecializationsPanel.set(!isCurrentlyOpen);
    } else if (item.expandable && item.children) {
      this.toggleMenuItem(index);
    } else if (item.link) {
      this.sidenav()?.close();
      this.router.navigate([item.link]);
    }
  }

  onChildItemClick(child: SidenavMenuItem, parentIndex: number, childIndex: number, event: Event): void {
    event.stopPropagation();
    if (child.expandable) {
      this.toggleChildItem(parentIndex, childIndex);
    } else if (child.link) {
      this.sidenav()?.close();
      this.router.navigate([child.link]);
    }
  }

  onGrandchildItemClick(grandchild: SidenavMenuItem, _parentIndex: number, _childIndex: number, _grandchildIndex: number, event: Event): void {
    event.stopPropagation();
    if (grandchild.link) {
      this.sidenav()?.close();
      this.router.navigate([grandchild.link]);
    }
  }

  // Subjects panel handlers
  onSubjectsBackClick(): void {
    this.showSubjectsPanel.set(false);
  }

  onSubjectClick(item: PanelItem): void {
    this.sidenav()?.close();
    this.showSubjectsPanel.set(false);
    this.router.navigate(['/topic', item.permalink]);
  }

  onShowAllSubjects(): void {
    this.sidenav()?.close();
    this.showSubjectsPanel.set(false);
    this.router.navigate(['/argomenti']);
  }

  onSubjectsSearch(searchTerm: string): void {
    this.sidenav()?.close();
    this.showSubjectsPanel.set(false);
    this.router.navigate(['/argomenti'], { queryParams: { search: searchTerm } });
  }

  // Specializations panel handlers
  onSpecializationsBackClick(): void {
    this.showSpecializationsPanel.set(false);
  }

  onSpecializationClick(item: PanelItem): void {
    this.sidenav()?.close();
    this.showSpecializationsPanel.set(false);
    this.router.navigate(['/topic', item.permalink]);
  }

  onShowAllSpecializations(): void {
    this.sidenav()?.close();
    this.showSpecializationsPanel.set(false);
    this.router.navigate(['/specializzazioni']);
  }

  onSpecializationsSearch(searchTerm: string): void {
    this.sidenav()?.close();
    this.showSpecializationsPanel.set(false);
    this.router.navigate(['/specializzazioni'], { queryParams: { search: searchTerm } });
  }

  // User info methods
  getUserInitials(): string {
    const user = this.user();
    if (!user || user.nome === 'Guest' || (!user.nome && !user.cognome)) {
      return '';
    }
    const firstInitial = user.nome ? user.nome.charAt(0).toUpperCase() : '';
    const lastInitial = user.cognome ? user.cognome.charAt(0).toUpperCase() : '';
    return `${firstInitial}${lastInitial}`;
  }

  getUserDisplayName(): string {
    const user = this.user();
    if (!user || user.nome === 'Guest') {
      return '';
    }
    return `${user.nome} ${user.cognome}, ${user.categoria || 'Dott.'}`;
  }

  isLoggedIn(): boolean {
    const user = this.user();
    return user && user.idAnagrafica !== -1 && user.nome !== 'Guest';
  }

  onUserProfileClick(): void {
    this.sidenav()?.close();
    this.userMenuClick.emit();
  }
}
